import React from "react";
import { gql } from "@apollo/client";
import compile from "lodash.template";
import { Remarkable } from "remarkable";
import get from "lodash.get";
import snakeCase from "lodash.snakecase";
import DateFnsUtils from "@date-io/date-fns";
import frLocale from "date-fns/locale/fr";
import enLocale from "date-fns/locale/en-GB";
import hoistNonReactStatics from "hoist-non-react-statics";

import { Alert, Divider, Paper, Typography } from "@mui/material";

import signatureTemplate from "components/user/signatureTemplate";

const md = new Remarkable({ html: true });

const dateFnsUtils = {
  fr: new DateFnsUtils({ locale: frLocale }),
  en: new DateFnsUtils({ locale: enLocale }),
};

export function compileTemplate(template, order, user, locale) {
  const tplRef = snakeCase(template.name);

  const variables = {
    // Functions
    debug: (variable) => `<pre>${JSON.stringify(variable, null, 2)}</pre>`,
    dateFnsUtils: dateFnsUtils[locale],
    get,

    // Variables
    order,
    lastSoundShaping: order.soundShapings[0],
    lastShipment: order.shipments[order.shipments.length - 1],
    user: order.user,
    me: user,
    tplRef,
  };

  return {
    variables,
    subject: compile(template.subject[locale])(variables),
    mdBody: compile(template.content[locale])(variables),
  };
}

function MailPreview(
  { user, order, locale, subject, mdBody, body, template, withSignature },
  ref
) {
  const compileTemplateRef = React.useCallback(
    (template) => {
      let orderLocale = (order.user && order.user.profile.locale) || "en";
      orderLocale = orderLocale.toLowerCase().startsWith("fr") ? "fr" : "en";

      return compileTemplate(template, order, user, locale || orderLocale);
    },
    [locale, order, user]
  );

  React.useImperativeHandle(
    ref,
    () => ({ compileTemplate: compileTemplateRef }),
    [compileTemplateRef]
  );

  let _subject = subject;
  let _mdBody = mdBody;
  let _body = body;

  if (template) {
    try {
      const compileResult = compileTemplateRef(template);

      _subject = compileResult.subject;
      _mdBody = compileResult.mdBody;
    } catch (error) {
      return (
        <Alert severity="error">
          <Typography color="inherit">{error.toString()}</Typography>
        </Alert>
      );
    }
  }

  if (_mdBody) {
    _body = md.render(_mdBody);
  }

  return (
    <Paper sx={{ p: 1 }}>
      <Typography>{_subject}</Typography>

      <Divider sx={{ m: (t) => t.spacing(1, -1) }} />

      <Typography component="div" dangerouslySetInnerHTML={{ __html: _body }} />

      {withSignature && (
        <div dangerouslySetInnerHTML={{ __html: signatureTemplate(user) }} />
      )}
    </Paper>
  );
}

MailPreview.compileTemplate = compileTemplate;

MailPreview.fragments = {
  order: gql`
    fragment MailPreview_order on Order {
      id

      user {
        id
        profile {
          email
          firstName
          lastName
          locale
        }
      }

      shippingAddress {
        country {
          id
          region
        }
      }

      soundShapings {
        id
      }

      shipments {
        id
        createdAt

        trackingId
        trackingUrl
      }

      invoice {
        id
        reference
      }
    }
  `,
  user: gql`
    fragment MailPreview_user on User {
      id

      ...signatureTemplate_user
    }

    ${signatureTemplate.fragments.user}
  `,
};

export default hoistNonReactStatics(React.forwardRef(MailPreview), MailPreview);
